import React, { useState, useEffect } from 'react';
import TriangleRuler from './TriangleRuler';
import CalibrationOverlay from './CalibrationOverlay';
import { getLocale, LocaleType } from '../locales';
import {
  autoDetectDevice,
  autoDetectMonitor,
  autoDetectByScreenPanel,
  isMobileDevice,
  loadCalibration,
  clearCalibration,
  calculateCssPixelsPerInch,
  saveCalibration,
  CalibrationSource,
  getConfidenceStars,
  getConfidenceLabel,
} from '../utils/screenDetection';

type UnitType = 'cm' | 'in';

interface AppWithLocaleProps {
  locale: LocaleType;
}

const AppWithLocale: React.FC<AppWithLocaleProps> = ({ locale }) => {
  const t = getLocale(locale);
  const [showControls, setShowControls] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [screenSize, setScreenSize] = useState(27);
  const [isMobile, setIsMobile] = useState(false);
  const [unit, setUnit] = useState<UnitType>('cm');
  const [detectedDevice, setDetectedDevice] = useState<string | null>(null);
  const [detectedMonitor, setDetectedMonitor] = useState<string | null>(null);
  const [calibrationSource, setCalibrationSource] = useState<CalibrationSource>('default');

  // Calibration state
  const [showCalibration, setShowCalibration] = useState(false);
  const [calibratedPPI, setCalibratedPPI] = useState<number | null>(null);
  const [calibratedDPR, setCalibratedDPR] = useState(0);
  const [isCalibrated, setIsCalibrated] = useState(false);

  // Reset caliper state
  const [resetKey, setResetKey] = useState(0);

  // ─── Initialization: try auto-detect → load calibration → or prompt ───
  useEffect(() => {
    const mobile = isMobileDevice();
    setIsMobile(mobile);

    (async () => {
      // Strategy 1: Auto-detect known device (mobile/tablet) — sync
      const device = autoDetectDevice();
      if (device) {
        setScreenSize(device.diagonalInches);
        setDetectedDevice(device.name);
        setCalibrationSource('device-db');
        return;
      }

      // Strategy 1.5: Screen panel fingerprint — sync, works for MacBook/Surface/etc
      const panel = autoDetectByScreenPanel();
      if (panel) {
        setCalibratedPPI(panel.cssPPI);
        setCalibratedDPR(window.devicePixelRatio);
        setIsCalibrated(true);
        setDetectedMonitor(panel.panel.name);
        setCalibrationSource('screen-panel');
        saveCalibration(panel.cssPPI, 'screen-panel');
        return;
      }

      // Strategy 2: Try autoDetectMonitor() — async, Chromium only
      try {
        const monitor = await autoDetectMonitor();
        if (monitor) {
          setCalibratedPPI(monitor.ppi);
          setCalibratedDPR(window.devicePixelRatio);
          setIsCalibrated(true);
          setDetectedMonitor(monitor.specs.name);
          setCalibrationSource('monitor-db');
          saveCalibration(monitor.ppi, 'monitor-db');
          return;
        }
      } catch {
        // getScreenDetails not available or permission denied
      }

      // Strategy 3: Load saved calibration from localStorage
      const saved = loadCalibration();
      if (saved) {
        setCalibratedPPI(saved.pxPerInch);
        setCalibratedDPR(saved.calibratedDPR);
        setIsCalibrated(true);
        setCalibrationSource(saved.source);
        return;
      }

      // Strategy 4: No data available — show calibration overlay for first-time users
      setShowCalibration(true);
    })();
  }, []);

  // Resize handler
  useEffect(() => {
    const handleResize = () => setIsMobile(isMobileDevice());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ─── Fullscreen (cross-browser) ───
  const getFullscreenElement = (): Element | null => {
    return document.fullscreenElement
      || (document as any).webkitFullscreenElement
      || null;
  };

  useEffect(() => {
    const handleFS = () =>
      setTimeout(() => setIsFullscreen(!!getFullscreenElement()), 100);
    setIsFullscreen(!!getFullscreenElement());
    document.addEventListener('fullscreenchange', handleFS);
    document.addEventListener('webkitfullscreenchange', handleFS);
    return () => {
      document.removeEventListener('fullscreenchange', handleFS);
      document.removeEventListener('webkitfullscreenchange', handleFS);
    };
  }, []);

  const toggleFullscreen = async () => {
    const el = document.documentElement;
    try {
      if (!getFullscreenElement()) {
        if (el.requestFullscreen) {
          await el.requestFullscreen();
        } else if ((el as any).webkitRequestFullscreen) {
          await (el as any).webkitRequestFullscreen();
        } else {
          console.warn('Fullscreen API is not supported by this browser.');
        }
      } else {
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if ((document as any).webkitExitFullscreen) {
          await (document as any).webkitExitFullscreen();
        }
      }
    } catch (err) {
      console.error('Fullscreen request failed:', err);
      setIsFullscreen(!!getFullscreenElement());
    }
  };

  // ─── Calibration callbacks ───
  const handleCalibrated = (ppi: number, source: CalibrationSource) => {
    setCalibratedPPI(ppi);
    setCalibratedDPR(window.devicePixelRatio);
    setIsCalibrated(true);
    setCalibrationSource(source);
    setShowCalibration(false);
  };

  // Real-time preview during calibration (slider adjustment)
  const handleCalibrationPreview = (ppi: number) => {
    setCalibratedPPI(ppi);
    setCalibratedDPR(window.devicePixelRatio);
  };

  const handleSkipCalibration = () => {
    // Guess screen size by resolution
    const maxDim = Math.max(window.screen.width, window.screen.height);
    let guessedDiag = 15.6;
    if (maxDim <= 1440) guessedDiag = 13.3;
    else if (maxDim <= 1600) guessedDiag = 14;
    else if (maxDim <= 2000) guessedDiag = 15.6;
    else if (maxDim <= 2600) guessedDiag = 24;
    else guessedDiag = 27;
    const ppi = calculateCssPixelsPerInch(guessedDiag);
    saveCalibration(ppi, 'default');
    setCalibratedPPI(ppi);
    setCalibratedDPR(window.devicePixelRatio);
    setIsCalibrated(true);
    setCalibrationSource('default');
    setShowCalibration(false);
  };

  const handleRecalibrate = () => {
    clearCalibration();
    setCalibratedPPI(null);
    setCalibratedDPR(0);
    setIsCalibrated(false);
    setDetectedDevice(null);
    setDetectedMonitor(null);
    setCalibrationSource('default');
    setShowCalibration(true);
  };

  const handleResetCalipers = () => {
    setResetKey(prev => prev + 1);
  };

  // ─── Confidence badge helpers ───
  const stars = getConfidenceStars(calibrationSource);
  const confidenceLabel = getConfidenceLabel(calibrationSource, locale);

  const renderStars = (filled: number) => {
    const total = 5;
    return '★'.repeat(filled) + '☆'.repeat(total - filled);
  };

  // Localized strings
  const strings = {
    en: {
      calibrate: 'Recalibrate',
      calibrated: 'Calibrated (card)',
      autoDetected: 'Auto-detected',
      monitorDetected: 'Screen identified',
      dragTip: 'Drag the triangle markers on the rulers to measure objects',
      accuracyHigh: 'High accuracy',
      accuracyMedium: 'Default accuracy',
      resetCalipers: 'Reset Calipers',
    },
    zh: {
      calibrate: '重新校准',
      calibrated: '已校准（银行卡）',
      autoDetected: '已自动检测',
      monitorDetected: '已识别屏幕',
      dragTip: '拖动标尺上的三角标记来测量物体',
      accuracyHigh: '高精度',
      accuracyMedium: '默认精度',
      resetCalipers: '重置卡尺',
    },
    nl: {
      calibrate: 'Opnieuw kalibreren',
      calibrated: 'Gekalibreerd (bankpas)',
      autoDetected: 'Automatisch gedetecteerd',
      monitorDetected: 'Scherm herkend',
      dragTip: 'Sleep de driehoekige markeringen op de linialen om objecten te meten',
      accuracyHigh: 'Hoge nauwkeurigheid',
      accuracyMedium: 'Standaard nauwkeurigheid',
      resetCalipers: 'Reset Schuifmaat',
    },
  };
  const s = strings[locale] || strings.en;

  // Determine which status badge to show
  const hasDetection = detectedDevice || detectedMonitor || isCalibrated;
  const displayName = detectedDevice || detectedMonitor;

  return (
    <div className="relative">
      {/* Canvas ruler */}
      <TriangleRuler
        screenSize={screenSize}
        locale={locale}
        unit={unit}
        calibratedPPI={calibratedPPI}
        calibratedDPR={calibratedDPR}
        resetKey={resetKey}
      />

      {/* Calibration overlay */}
      {showCalibration && (
        <CalibrationOverlay
          locale={locale}
          onCalibrated={handleCalibrated}
          onCancel={handleSkipCalibration}
          onPreview={handleCalibrationPreview}
        />
      )}

      {/* ───── Floating Control Panel (dark glassmorphism) ───── */}
      {showControls && !showCalibration && (
        <div
          className="fixed bottom-6 right-6 w-72 bg-gray-900/90 backdrop-blur-xl rounded-2xl shadow-2xl shadow-black/20 border border-white/10 z-10 p-4"
        >
          {/* Header */}
          <div className="flex justify-between items-center mb-3">
            <h2 className="text-sm font-semibold text-white tracking-wide">
              📐 {t.controls.title}
            </h2>
            <button
              onClick={() => setShowControls(false)}
              className="w-6 h-6 flex items-center justify-center rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition-colors text-xs"
            >
              ✕
            </button>
          </div>

          {/* Status badge */}
          {hasDetection && (
            <div className="mb-3 px-3 py-2.5 bg-white/5 border border-white/10 rounded-xl">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-white/10 text-emerald-400 text-[10px] font-bold flex-shrink-0">✓</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1 text-xs">
                    <span className="font-medium text-white/90">
                      {detectedDevice ? s.autoDetected : detectedMonitor ? s.monitorDetected : s.calibrated}
                    </span>
                    {displayName && (
                      <>
                        <span className="text-white/40">·</span>
                        <strong className="text-white truncate text-[11px]">{displayName}</strong>
                        {detectedDevice && (
                          <span className="text-white/40 text-[10px]">({screenSize}")</span>
                        )}
                      </>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-amber-400 text-[11px] tracking-wider leading-none">{renderStars(stars)}</span>
                    <span className="text-white/40 text-[10px]">{confidenceLabel}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Drag tip — subtle one-liner */}
          <p className="mb-3 text-[11px] text-gray-500 text-center leading-tight">
            {s.dragTip}
          </p>

          {/* Unit toggle — segmented control */}
          <div className="mb-3">
            <div className="flex bg-white/5 rounded-xl p-1">
              <button
                onClick={() => setUnit('cm')}
                className={`flex-1 py-1.5 rounded-lg text-xs font-medium transition-all ${unit === 'cm'
                  ? 'bg-blue-500 text-white shadow-sm shadow-blue-500/25'
                  : 'text-gray-400 hover:text-white'
                  }`}
              >
                {t.controls.centimeters}
              </button>
              <button
                onClick={() => setUnit('in')}
                className={`flex-1 py-1.5 rounded-lg text-xs font-medium transition-all ${unit === 'in'
                  ? 'bg-blue-500 text-white shadow-sm shadow-blue-500/25'
                  : 'text-gray-400 hover:text-white'
                  }`}
              >
                {t.controls.inches}
              </button>
            </div>
          </div>

          {/* Action buttons row */}
          <div className="flex gap-2">
            <button
              onClick={handleResetCalipers}
              className="py-2 px-3 rounded-xl text-xs font-medium transition-all bg-white/5 hover:bg-white/10 text-gray-300 hover:text-white border border-white/5 hover:border-white/10 flex items-center justify-center gap-1.5"
              title={s.resetCalipers}
            >
              <span>↺</span> {s.resetCalipers}
            </button>
            <button
              onClick={handleRecalibrate}
              className="flex-1 py-2 px-3 rounded-xl text-xs font-medium transition-all bg-white/5 hover:bg-white/10 text-gray-300 hover:text-white border border-white/5 hover:border-white/10 flex items-center justify-center gap-1.5"
            >
              <span>🎯</span> {s.calibrate}
            </button>
            {!isMobile && (
              <button
                onClick={toggleFullscreen}
                className={`py-2 px-3 rounded-xl text-xs font-medium transition-all border flex items-center justify-center gap-1.5 ${isFullscreen
                  ? 'bg-red-500/20 hover:bg-red-500/30 text-red-300 hover:text-red-200 border-red-500/20'
                  : 'bg-white/5 hover:bg-white/10 text-gray-300 hover:text-white border-white/5 hover:border-white/10'
                  }`}
              >
                <span>{isFullscreen ? '⛶' : '⛶'}</span>
                {isFullscreen ? t.common.exitFullscreen : t.common.fullscreen}
              </button>
            )}
          </div>
        </div>
      )}

      {/* ───── FABs when panel is closed ───── */}
      {!showControls && !showCalibration && (
        <div className="fixed z-10 bottom-6 right-6 flex flex-col items-center gap-3">
          {!isMobile && (
            <button
              onClick={toggleFullscreen}
              className={`w-10 h-10 rounded-full backdrop-blur-md shadow-lg flex items-center justify-center text-sm transition-all hover:scale-105 ${isFullscreen
                ? 'bg-red-500/80 hover:bg-red-500 text-white shadow-red-500/20'
                : 'bg-gray-900/80 hover:bg-gray-900 text-gray-300 hover:text-white border border-white/10 shadow-black/20'
                }`}
              title={isFullscreen ? t.common.exitFullscreen : t.common.fullscreen}
            >
              ⛶
            </button>
          )}
          <button
            onClick={() => setShowControls(true)}
            className="w-12 h-12 rounded-full bg-gray-900/80 backdrop-blur-md shadow-lg shadow-black/20 border border-white/10 flex items-center justify-center text-lg text-gray-300 hover:text-white hover:bg-gray-900 hover:shadow-xl hover:scale-105 transition-all"
            title={t.common.control}
          >
            ⚙️
          </button>
        </div>
      )}
    </div>
  );
};

export default AppWithLocale;
